import { useEffect, useMemo, useState } from "react";
import { makeStyles } from "tss-react/mui";
import { MiButton } from "@miplatform/components";
import {
  BUTTON_COLORS,
  BUTTON_VARIANTS,
  ICONS_WEB,
  OPTION_STATUS,
  TYPOGRAPHY_VARIANTS,
} from "@miplatform/constants";
import * as mdi from "@mdi/js";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Typography,
  useTheme,
} from "@mui/material";
import { CollapsibleIcon } from "../../../../components/CollapsibleIcon/CollapsibleIcon";
import { PlanMaterialOption } from "./PlanMaterialOption/PlanMaterialOption";
import { usePlanAreaOptionViewModel } from "./usePlanAreaOptionViewModel";

export const PlanAreaOption = ({
  area = {},
  isLoading,
  stateManager,
  isEditing,
  selectedAreaId,
  showOptionSummary,
  showAreaDrawer,
  showCategoryFilter,
  handleOpenCategoryFilter = () => {},
  handleRemoveAreaFromOption = () => {},
  handleUndoRemoveAreaFromOption = () => {},
  handleRemoveItemFromArea = () => {},
  handleRemoveAllItemsFromArea = () => {},
  handleReturnItemToPlan = () => {},
  handleReturnAllItemsToPlan = () => {},
}) => {
  const { optionStatus } = area;
  const [isExpanded, setIsExpanded] = useState(true);
  const key = area.id + optionStatus;

  const { getMaterials, setMaterialsData, materialsData } =
    usePlanAreaOptionViewModel({
      area,
      stateManager,
    });
  const { classes } = useStyles();
  const theme = useTheme();

  useEffect(() => {
    if (area.materials?.length > 0) {
      getMaterials();
    } else {
      setMaterialsData([]);
    }
  }, [JSON.stringify(area.materials)]);

  useEffect(() => {
    if (!isExpanded) {
      toggleExpand();
    }
  }, [materialsData]);

  const isExistingWithRemovedItems = useMemo(() => {
    return (
      materialsData.some(
        (item) => item.optionStatus === OPTION_STATUS.REMOVED
      ) && optionStatus === OPTION_STATUS.EXISTING
    );
  }, [materialsData, optionStatus]);

  const toggleExpand = () => {
    setIsExpanded((prev) => !prev);
  };

  const backgroundColor = `classes.accordionContainer ${
    optionStatus === OPTION_STATUS.ADDED ? classes.isAdded : ""
  } 
  ${optionStatus === OPTION_STATUS.REMOVED ? classes.isRemoved : ""}`;

  const disabledWhenAddingItems =
    isLoading || showCategoryFilter || showOptionSummary;
  const noMaterials = materialsData?.length === 0;
  const allMaterialsRemoved = materialsData?.every(
    (item) => item.optionStatus === OPTION_STATUS.REMOVED
  );

  const getLeftActions = () => {
    switch (optionStatus) {
      case OPTION_STATUS.REMOVED:
        return (
          <MiButton
            title="Undo Remove"
            variant={BUTTON_VARIANTS.TEXT}
            onClick={() => handleUndoRemoveAreaFromOption()}
            disabled={disabledWhenAddingItems}
          />
        );
      case OPTION_STATUS.ADDED:
        return (
          <MiButton
            title="Delete Area"
            variant={BUTTON_VARIANTS.TEXT}
            color={BUTTON_COLORS.ERROR}
            onClick={handleRemoveAreaFromOption}
            disabled={disabledWhenAddingItems}
          />
        );
      case OPTION_STATUS.EXISTING:
      default:
        return (
          <MiButton
            title="Remove Area"
            variant={BUTTON_VARIANTS.TEXT}
            color={BUTTON_COLORS.ERROR}
            onClick={handleRemoveAreaFromOption}
            disabled={disabledWhenAddingItems}
          />
        );
    }
  };

  const getRightActions = () => {
    const buttons = [];
    switch (optionStatus) {
      case OPTION_STATUS.REMOVED:
        return null;
      case OPTION_STATUS.EXISTING:
      case OPTION_STATUS.ADDED:
      default:
        if (isExistingWithRemovedItems && allMaterialsRemoved) {
          buttons.push(
            <MiButton
              title="Return All Items"
              variant={BUTTON_VARIANTS.TEXT}
              onClick={handleReturnAllItemsToPlan}
              disabled={disabledWhenAddingItems}
            />
          );
        } else {
          buttons.push(
            <MiButton
              title="Remove All Items"
              key={`removeAllBtn${key}`}
              variant={BUTTON_VARIANTS.TEXT}
              color="error"
              onClick={() => {
                handleRemoveAllItemsFromArea();
              }}
              disabled={disabledWhenAddingItems || noMaterials}
            />
          );
        }
        buttons.push(
          <MiButton
            title="Add Items"
            key={`addBtn${key}`}
            variant={BUTTON_VARIANTS.TEXT}
            startIcon={{
              path: mdi[ICONS_WEB.PLUS],
              iconSize: 0.8,
            }}
            onClick={handleOpenCategoryFilter}
            disabled={showAreaDrawer || isLoading || showOptionSummary}
          />
        );
    }
    return buttons;
  };

  return (
    <Accordion
      className={backgroundColor}
      expanded={isExpanded}
      disableGutters
      style={{
        border:
          selectedAreaId === area.id
            ? `2px solid ${theme.palette.orange.main}`
            : "2px solid transparent",
      }}
    >
      <AccordionSummary className={classes.accordionSummary}>
        <Box
          flex={1}
          display="flex"
          alignItems="center"
          gap={theme.spacing(2)}
          paddingLeft={theme.spacing(2)}
        >
          <Typography
            fontWeight={theme.fontWeight.semiBold}
            fontSize={theme.fontSize.medium}
            color={
              optionStatus === OPTION_STATUS.REMOVED
                ? theme.palette.red.dark
                : theme.palette.black.main
            }
            sx={{
              textDecoration:
                optionStatus === OPTION_STATUS.REMOVED
                  ? "line-through"
                  : "none",
            }}
          >
            {area.label}
          </Typography>
          {isEditing && getLeftActions()}
        </Box>
        <Box display="flex" alignItems="center">
          {isEditing && getRightActions()}
          <CollapsibleIcon isCollapsed={isExpanded} onCollapse={toggleExpand} />
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        {materialsData?.map((item) => {
          return (
            <PlanMaterialOption
              key={item.id + item.optionStatus}
              item={item}
              area={area}
              disableActions={isLoading || showOptionSummary}
              isEditing={isEditing}
              handleRemoveItemFromArea={handleRemoveItemFromArea}
              handleReturnItemToPlan={handleReturnItemToPlan}
            />
          );
        })}
        {noMaterials && (
          <Box className={classes.emptyMaterialsContainer}>
            <Typography
              variant={TYPOGRAPHY_VARIANTS.SUBTITLE2}
              color="textSecondary"
              padding={theme.spacing(4)}
            >
              Add items to the Area to begin creating a modification.
            </Typography>
          </Box>
        )}
      </AccordionDetails>
    </Accordion>
  );
};

const useStyles = makeStyles()((theme) => ({
  accordionContainer: {
    backgroundColor: theme.palette.common.white,
    width: "100%",
    margin: 0,
    padding: 4,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    borderRadius: 4,
    "&::before": {
      content: "none",
    },
    "&.Mui-expanded": {
      margin: 0,
    },
    "& .MuiAccordionSummary-content": {
      margin: 0,
      alignItems: "center",
    },
    "& .MuiCollapse-root": {
      width: "100%",
    },
  },
  accordionSummary: {
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    paddingTop: theme.spacing(1),
    "&.Mui-focusVisible": {
      backgroundColor: theme.palette.common.white,
    },
  },
  emptyMaterialsContainer: {
    padding: theme.spacing(0, 2),
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: theme.palette.common.white,
    border: `1px dashed ${theme.palette.grey.accent}`,
  },
  isAdded: {
    backgroundColor: theme.palette.green.light,
  },
  isRemoved: {
    backgroundColor: theme.palette.red.light,
  },
}));
